
<template>
  <div
    class="text-field"
    :class="{
      'text-field--focused': isFocused,
      'text-field--filled': hasValue,
      'text-field--dark': isDark,
      'text-field--disabled': isDisabled,
      'text-field--invalid': isInvalid
    }"
    :style="rootStyle"
  >
    <span
      class="text-field__icon"
      aria-hidden="true"
    >
      <!-- Email -->
      <svg
        v-if="iconType === 'email'"
        viewBox="0 0 24 24"
        fill="none"
      >
        <rect
          x="3"
          y="5"
          width="18"
          height="14"
          rx="3"
          stroke="currentColor"
          stroke-width="1.6"
        />

        <path
          d="M4 7L12 13L20 7"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>

      <!-- Password -->
      <svg
        v-else-if="iconType === 'password'"
        viewBox="0 0 24 24"
        fill="none"
      >
        <rect
          x="5"
          y="10"
          width="14"
          height="11"
          rx="3"
          stroke="currentColor"
          stroke-width="1.6"
        />

        <path
          d="M8 10V7C8 4.8 9.8 3 12 3C14.2 3 16 4.8 16 7V10"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />

        <circle
          cx="12"
          cy="15.5"
          r="1.25"
          fill="currentColor"
        />
      </svg>

      <!-- Phone -->
      <svg
        v-else-if="iconType === 'phone'"
        viewBox="0 0 24 24"
        fill="none"
      >
        <rect
          x="7"
          y="2"
          width="10"
          height="20"
          rx="3"
          stroke="currentColor"
          stroke-width="1.6"
        />

        <path
          d="M10 5H14M11 19H13"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>

      <!-- Search -->
      <svg
        v-else-if="iconType === 'search'"
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          cx="10.8"
          cy="10.8"
          r="6.8"
          stroke="currentColor"
          stroke-width="1.6"
        />

        <path
          d="M16 16L21 21"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>

      <!-- Default -->
      <svg
        v-else
        viewBox="0 0 24 24"
        fill="none"
      >
        <circle
          cx="12"
          cy="8"
          r="3.5"
          stroke="currentColor"
          stroke-width="1.6"
        />

        <path
          d="M5 20C5.5 16.3 8 14.5 12 14.5C16 14.5 18.5 16.3 19 20"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    </span>

    <input
      v-bind="inputAttrs"
      :id="fieldId"
      v-model="inputValue"
      class="text-field__input"
      :class="{
        'text-field__input--raised': isActive
      }"
      :type="type"
      :disabled="isDisabled"
      :aria-label="placeholder || undefined"
      :aria-invalid="isInvalid || undefined"
      @focus="handleFocus"
      @blur="handleBlur"
    />

    <label
      v-if="placeholder"
      :for="fieldId"
      class="text-field__label"
      :class="{
        'text-field__label--active': isActive
      }"
    >
      {{ placeholder }}
    </label>

    <span class="text-field__focus-line"></span>
  </div>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  useAttrs,
  useId
} from "vue";

defineOptions({
  inheritAttrs: false
});

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ""
  },

  placeholder: {
    type: String,
    default: ""
  },

  type: {
    type: String,
    default: "text"
  },

  icon: {
    type: String,
    default: ""
  },

  disabled: {
    type: Boolean,
    default: false
  },

  invalid: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([
  "update:modelValue",
  "focus",
  "blur"
]);

const attrs = useAttrs();

const generatedId = useId();

const isFocused = ref(false);
const isDark = ref(false);

let themeObserver = null;

// MARK: - Model

const inputValue = computed({
  get() {
    return props.modelValue ?? "";
  },

  set(value) {
    emit("update:modelValue", value);
  }
});

// MARK: - Attributes

const fieldId = computed(() => {
  return attrs.id
    ? String(attrs.id)
    : `text-field-${generatedId}`;
});

const rootStyle = computed(() => {
  return attrs.style || undefined;
});

const inputAttrs = computed(() => {
  const result = { ...attrs };

  delete result.class;
  delete result.style;
  delete result.id;
  delete result.disabled;
  delete result["aria-invalid"];

  return result;
});

const isDisabled = computed(() => {
  return (
    props.disabled ||
    attrs.disabled === "" ||
    attrs.disabled === true
  );
});

const isInvalid = computed(() => {
  return (
    props.invalid ||
    attrs["aria-invalid"] === true ||
    attrs["aria-invalid"] === "true"
  );
});

// MARK: - Input State

const hasValue = computed(() => {
  return String(inputValue.value).length > 0;
});

const isActive = computed(() => {
  return isFocused.value || hasValue.value;
});

// MARK: - Icon

const iconType = computed(() => {
  if (props.icon) {
    return props.icon;
  }

  const text = props.placeholder.toLowerCase();

  if (
    props.type === "email" ||
    text.includes("email") ||
    text.includes("почт")
  ) {
    return "email";
  }

  if (
    props.type === "password" ||
    text.includes("парол")
  ) {
    return "password";
  }

  if (
    props.type === "tel" ||
    text.includes("телефон")
  ) {
    return "phone";
  }

  if (
    props.type === "search" ||
    text.includes("поиск")
  ) {
    return "search";
  }

  return "user";
});

// MARK: - Focus

function handleFocus(event) {
  isFocused.value = true;
  emit("focus", event);
}

function handleBlur(event) {
  isFocused.value = false;
  emit("blur", event);
}

// MARK: - Theme

function syncTheme() {
  isDark.value =
    document.documentElement.getAttribute("data-theme") === "dark";
}

onMounted(() => {
  syncTheme();

  themeObserver = new MutationObserver(syncTheme);

  themeObserver.observe(
    document.documentElement,
    {
      attributes: true,
      attributeFilter: ["data-theme"]
    }
  );
});

onBeforeUnmount(() => {
  themeObserver?.disconnect();
  themeObserver = null;
});
</script>

<style scoped>
/* ==========================================================
   TEXT FIELD — LOCAL THEME
   ========================================================== */

.text-field {
  --tf-bg-top: var(--ae-input, #f5f7fb);
  --tf-bg-bottom: var(--ae-input, #eef1f6);
  --tf-text: var(--ae-text, #242d3d);
  --tf-muted: var(--ae-muted, #939eaf);
  --tf-border: var(--ae-line, #e5eaf2);
  --tf-accent: #4c77de;

  position: relative;

  display: flex;
  align-items: center;

  width: 100%;
  min-width: 0;
  height: 52px;

  padding: 0 16px 0 49px;

  box-sizing: border-box;

  overflow: hidden;

  border: 1px solid var(--tf-border);
  border-radius: 12px;

  background: linear-gradient(
    180deg,
    var(--tf-bg-top),
    var(--tf-bg-bottom)
  );

  color: var(--tf-text);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    0 3px 10px rgba(30, 45, 75, 0.025);

  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
}

/* ==========================================================
   DARK THEME
   ========================================================== */

.text-field--dark {
  --tf-bg-top: var(--ae-input, #2c323d);
  --tf-bg-bottom: var(--ae-input, #272d37);
  --tf-text: var(--ae-text, #eef1f6);
  --tf-muted: var(--ae-muted, #9ca7b8);
  --tf-border: var(--ae-line, #3a4351);
  --tf-accent: #8eabff;

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.025),
    0 3px 10px rgba(0, 0, 0, 0.05);
}

/* ==========================================================
   HOVER / FOCUS
   ========================================================== */

.text-field:hover:not(.text-field--disabled) {
  border-color: #9fb9eb;
}

.text-field--dark:hover:not(.text-field--disabled) {
  border-color: #647da9;
}

.text-field--focused {
  border-color: var(--tf-accent);

  box-shadow:
    0 0 0 3px rgba(76, 119, 222, 0.10),
    0 5px 18px rgba(76, 119, 222, 0.06);
}

.text-field--dark.text-field--focused {
  box-shadow:
    0 0 0 3px rgba(142, 171, 255, 0.12),
    0 5px 18px rgba(0, 0, 0, 0.10);
}

.text-field--disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.text-field--invalid {
  border-color: #d95f76;
}

.text-field--invalid.text-field--focused {
  box-shadow:
    0 0 0 3px rgba(217, 95, 118, 0.10);
}

/* ==========================================================
   ICON
   ========================================================== */

.text-field__icon {
  position: absolute;
  z-index: 2;

  left: 15px;
  top: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 21px;
  height: 21px;

  color: var(--tf-muted);

  transform: translateY(-50%);

  pointer-events: none;

  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.text-field__icon svg {
  display: block;

  width: 20px;
  height: 20px;

  flex-shrink: 0;
}

.text-field--focused .text-field__icon {
  color: var(--tf-accent);

  transform:
    translateY(-50%)
    scale(1.04);
}

/* ==========================================================
   INPUT
   ========================================================== */

.text-field__input {
  position: relative;
  z-index: 2;

  display: block;

  width: 100%;
  min-width: 0;
  height: 100%;

  margin: 0;
  padding: 0;

  box-sizing: border-box;

  border: 0;
  outline: 0;

  background: transparent;

  color: var(--tf-text);
  caret-color: var(--tf-accent);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  font-weight: normal;
  line-height: 1.4;

  transition: padding-top 0.2s ease;
}

.text-field__input--raised {
  padding-top: 14px;
}

.text-field__input:disabled {
  cursor: not-allowed;
}

.text-field__input::placeholder {
  color: transparent;
}

/* ==========================================================
   FLOATING LABEL
   ========================================================== */

.text-field__label {
  position: absolute;
  z-index: 2;

  left: 49px;
  top: 50%;

  max-width: calc(100% - 65px);

  overflow: hidden;

  color: var(--tf-muted);

  font-family: "Eastman-Regular", sans-serif;
  font-size: 13px;
  font-weight: normal;
  line-height: 1.2;

  white-space: nowrap;
  text-overflow: ellipsis;

  pointer-events: none;

  transform: translateY(-50%);

  transition:
    top 0.2s ease,
    transform 0.2s ease,
    font-size 0.2s ease,
    color 0.2s ease;
}

.text-field__label--active {
  top: 10px;

  color: var(--tf-muted);

  font-size: 9px;

  transform: translateY(0);
}

.text-field--focused .text-field__label--active {
  color: var(--tf-accent);
}

/* ==========================================================
   FOCUS LINE
   ========================================================== */

.text-field__focus-line {
  position: absolute;
  z-index: 1;

  left: 15px;
  right: 15px;
  bottom: 0;

  height: 2px;

  border-radius: 2px;

  background: var(--tf-accent);

  opacity: 0;

  transform: scaleX(0.45);

  transition:
    opacity 0.22s ease,
    transform 0.22s ease;

  pointer-events: none;
}

.text-field--focused .text-field__focus-line {
  opacity: 1;
  transform: scaleX(1);
}

/* ==========================================================
   AUTOFILL
   ========================================================== */

.text-field__input:-webkit-autofill,
.text-field__input:-webkit-autofill:hover,
.text-field__input:-webkit-autofill:focus {
  -webkit-text-fill-color: var(--tf-text);

  box-shadow:
    0 0 0 100px var(--tf-bg-bottom) inset;

  caret-color: var(--tf-accent);
}

/* ==========================================================
   MOBILE
   ========================================================== */

@media (max-width: 600px) {
  .text-field {
    height: 50px;
    border-radius: 11px;
  }

  .text-field__input,
  .text-field__label {
    font-size: 12px;
  }

  .text-field__label--active {
    font-size: 9px;
  }
}

/* ==========================================================
   REDUCED MOTION
   ========================================================== */

@media (prefers-reduced-motion: reduce) {
  .text-field,
  .text-field__icon,
  .text-field__input,
  .text-field__label,
  .text-field__focus-line {
    transition: none;
  }
}
</style>
